import { Box, FormControl, Stack, styled, useThemeProps } from '@mui/material';
import type { ComponentsPropsList } from '@mui/material/styles';
import { FORM_FIELD_THEME_NAME } from '@onevision-resources/design-theme';
import { parseScheduleWindows } from '@provision/sidekick-utils';
import {
  MaterialValueEditor,
  MaterialValueEditorProps,
  MaterialValueSelectorProps,
} from '@react-querybuilder/material';
import { Dayjs } from 'dayjs';
import React, { ComponentProps, useEffect, useId, useMemo, useState } from 'react';
import { FullField, TestID, toFlatOptionArray } from 'react-querybuilder';
import { isNonNullish } from 'remeda';
import { ConditionTextField } from 'src/components/ConditionsBuilder';
import { FormFieldLabel } from 'src/components/Forms/FormFieldLabel/form-field-label';
import { ScheduleConditionEditor } from 'src/components/ScheduleConditionEditor';
import { SelectMenu, type SelectMenuOption } from 'src/components/SelectMenu';
import {
  BehaviorConfigOption,
  FormControlType,
  RuleControlType,
  SelectorValue,
} from 'src/graphql';
import {
  ConditionType,
  DataConditionOperator,
} from 'src/graphql/entities/behavior/behavior-config/behavior-config.types';
import { isValidHttpUrl } from 'src/utils/url';
import { RulesDatePicker } from './components/rules-date-picker';
import { RulesDateTimePicker } from './components/rules-date-time-picker';
import { RULE_ATTRIBUTE_FIELD_CLASS } from './rule-field-label-space';
import { UseQueryOptions } from './use-rules-engine';

export type OptionValue = {
  label: string;
  group: string;
} & ({ value: string } | { value: number } | { value: boolean });

const EMPTY_OPTION_VALUE: OptionValue = {
  value: '',
  group: '',
  label: '',
};

const NO_FORM_FIELD_PROPS: ComponentsPropsList['OvFormField'] = {};

// Operators and AND / OR are short words: the menu fits its rows instead of the default width.
const SHORT_MENU_MIN_WIDTH: Partial<Record<string, string>> = {
  [TestID.combinators]: '6rem',
  [TestID.operators]: '8rem',
};

// Name set by react-querybuilder to all added conditions / actions under `field` value
export const RQB_NEW_FIELD_NAME = 'new';

export type CustomValueSelectorProps = MaterialValueSelectorProps & {
  value: FullField;
  options: OptionValue[];
  testID: string;
  handleOnChange: (value: OptionValue) => void;
};
export const CustomValueSelector = (
  /**
   *  Providing own typings for specific properties
   *  to narrow typings related to MUI components used for customization.
   */
  props: CustomValueSelectorProps,
): React.JSX.Element | null => {
  const { options, value } = props;
  const triggerId = useId();
  const { labelAbove } = useThemeProps({
    props: NO_FORM_FIELD_PROPS,
    name: FORM_FIELD_THEME_NAME,
  });
  const option =
    useMemo(() => {
      return options.find((item) => item.value === value)!;
    }, [options, value]) || EMPTY_OPTION_VALUE;

  if (props.field === RQB_NEW_FIELD_NAME && !props.value) {
    return null;
  }

  // `fields` id is used for the attribute selector component in the query builder.
  if (props.testID === TestID.fields) {
    // react-querybuilder assigns `~` string to `props.value` for value editor component,
    // when it is mounted and no option has been specified yet. Probably, because it expects for
    // the component to be a text input.
    // We need to provide an empty string to the Autocomplete component to comply with the type of
    // options array and/or not omit this value completely, because in that case form control
    // would be changed from uncontrolled to controlled.
    const optionValue = typeof value === 'string' ? option : value;

    // Leaving empty placeholder option on top, and making the rest of the options
    // properly grouped (Autocomplete doesn't do it on it's own).
    const sortedOptions = [...options].sort((a, b) =>
      !a.group ? -1 : a.group.localeCompare(b.group),
    );

    const menuOptions: SelectMenuOption[] = sortedOptions
      .filter((item) => String(item.value) !== '')
      .map((item) => ({
        value: String(item.value),
        // the menu renders `group` as the section header, so drop the duplicated group prefix
        // from the item itself (e.g. "Conversation / Subject" -> "Subject").
        title:
          item.group && item.label.startsWith(`${item.group} / `)
            ? item.label.slice(item.group.length + 3)
            : item.label,
        group: item.group || undefined,
      }));
    const pickedKey =
      optionValue && String(optionValue.value) !== '' ? String(optionValue.value) : null;

    return (
      <Box
        className={RULE_ATTRIBUTE_FIELD_CLASS}
        sx={{ position: 'relative', minWidth: { xs: 0, sm: '9rem' } }}
      >
        {labelAbove ? (
          <FormFieldLabel
            htmlFor={triggerId}
            sx={{ position: 'absolute', bottom: '100%', left: 0, whiteSpace: 'nowrap' }}
          >
            Attribute
          </FormFieldLabel>
        ) : null}
        <SelectMenu
          id={triggerId}
          label="Attribute"
          variant="field"
          size="small"
          options={menuOptions}
          sectioned
          value={pickedKey}
          placeholder={labelAbove ? undefined : 'Attribute'}
          disabled={props.disabled}
          onChange={(key) => {
            const picked = sortedOptions.find((item) => String(item.value) === key);
            if (picked) {
              props.handleOnChange(picked);
            }
          }}
        />
      </Box>
    );
  }

  const flatOptions = toFlatOptionArray(props.options);

  return (
    <Box
      className={props.className}
      title={props.title}
      sx={{
        flexShrink: 0,
        ...(props.testID === TestID.combinators
          ? { width: '5.5rem' }
          : { minWidth: { xs: 0, sm: '8rem' } }),
      }}
    >
      <SelectMenu
        label={props.title ?? 'Value'}
        variant="field"
        size="small"
        options={flatOptions.map((item) => ({ value: item.name, title: item.label }))}
        value={typeof value === 'string' ? value : null}
        menuWidth={SHORT_MENU_MIN_WIDTH[props.testID] ? 'max-content' : undefined}
        menuSx={{ minWidth: SHORT_MENU_MIN_WIDTH[props.testID] }}
        disabled={props.disabled}
        onChange={(key) => props.handleOnChange(key)}
      />
    </Box>
  );
};

const FieldsContainer = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'fillCell',
})<{ fillCell?: boolean }>(({ theme, fillCell }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1),
  minWidth: '13rem',
  [theme.breakpoints.down('sm')]: {
    minWidth: 0,
    flexWrap: 'wrap',
  },
  // a condition's value fills the builder's value column and shrinks with it
  ...(fillCell && { width: '100%', minWidth: 0, flexWrap: 'nowrap' }),
}));

const RANGE_OPERATORS = [DataConditionOperator.Between, DataConditionOperator.NotBetween];

function toTextValue(value: unknown): string {
  return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
}

const MULTIPLE_CHOICE_OPERATORS = [
  DataConditionOperator.IsOneOf,
  DataConditionOperator.IsNotOneOf,
  DataConditionOperator.In,
  DataConditionOperator.NotIn,
];

// An array-typed custom attribute (e.g. multi_select) carries an array write/read cast such as
// `::text[]` on its accessor and always edits as a multi-value picker. Conditions reach this via
// the multi-choice operators above, but an action has no operator, so detect it from the cast.
const ARRAY_CAST_ACCESSOR_PATTERN = /::\w+\[\]$/;

const toOptionKey = (option: Pick<BehaviorConfigOption, 'value'>) => String(option.value);

export const CustomValueEditor = (
  props: Omit<MaterialValueEditorProps, 'value' | 'context'> & {
    field: SelectorValue;
    value: string | OptionValue | OptionValue[];
    operator: DataConditionOperator;
    context: { timezone?: string; queryOptions: UseQueryOptions };
  },
) => {
  const [inputValue, setInputValue] = useState('');

  // Reset input value when field changes OR operator changes
  useEffect(() => {
    setInputValue('');
  }, [props.field?.value, props.operator]);

  const { sx } = getFieldStyles(props);
  const { timezone, queryOptions } = props.context;

  const optionsData = (() => {
    if (!props.field || !props.field.optionsQuery) {
      return undefined;
    }
    return props.context.queryOptions[props.field.optionsQuery];
  })();

  const options = useMemo(() => {
    if (!props.field) return [];

    if (props.field.optionsQuery) {
      return optionsData?.options;
    }
    if (props.field.fieldOptions?.length) {
      return props.field.fieldOptions;
    }
    return [];
  }, [optionsData?.options, props.field]);

  const isLoadingOptions = Boolean(optionsData?.isLoading);

  if (!props.field) return null;

  if (props.field === RQB_NEW_FIELD_NAME && !props.value) {
    return null;
  }

  let onInputChange: null | ((query: string) => void) = null;

  if (typeof props.field.optionsQuery !== 'undefined') {
    const optionsData = queryOptions[props.field.optionsQuery];
    if ('onInputChange' in optionsData) {
      onInputChange = optionsData.onInputChange || null;
    }
  }

  const isCondition = props.field.controlType === RuleControlType.ConditionControl;
  const actionPrefix =
    props.field.controlType === RuleControlType.ActionControl ? 'to ' : null;
  const textFieldSx = isCondition ? undefined : sx;

  if (
    props.field.uiType === FormControlType.Number &&
    RANGE_OPERATORS.includes(props.operator)
  ) {
    const [from = '', to = ''] = toTextValue(props.value).split(',');
    return (
      <FieldsContainer fillCell={isCondition} title={props.title}>
        {actionPrefix}
        <ConditionTextField
          type="number"
          placeholder="From"
          value={from}
          disabled={props.disabled}
          onChange={(next) => props.handleOnChange(`${next},${to}`)}
          sx={textFieldSx}
        />
        and
        <ConditionTextField
          type="number"
          placeholder="To"
          value={to}
          disabled={props.disabled}
          onChange={(next) => props.handleOnChange(`${from},${next}`)}
          sx={textFieldSx}
        />
      </FieldsContainer>
    );
  }

  if (
    props.field.uiType === FormControlType.Text ||
    props.field.uiType === FormControlType.Number
  ) {
    return (
      <FieldsContainer fillCell={isCondition}>
        {actionPrefix}
        <ConditionTextField
          className={props.className}
          title={props.title}
          type={props.field.uiType === FormControlType.Number ? 'number' : undefined}
          value={toTextValue(props.value)}
          disabled={props.disabled}
          onChange={props.handleOnChange}
          sx={textFieldSx}
        />
      </FieldsContainer>
    );
  }

  if (props.field.uiType === FormControlType.Url) {
    const urlValue = toTextValue(props.value);
    const isInvalidUrl = urlValue.length > 0 && !isValidHttpUrl(urlValue);
    return (
      <FieldsContainer fillCell={isCondition}>
        {actionPrefix}
        <ConditionTextField
          className={props.className}
          title={props.title}
          value={urlValue}
          error={isInvalidUrl}
          helperText={isInvalidUrl ? 'Enter a valid URL, e.g. https://example.com' : undefined}
          disabled={props.disabled}
          onChange={props.handleOnChange}
          sx={textFieldSx}
        />
      </FieldsContainer>
    );
  }
  if (props.field.uiType === FormControlType.List) {
    const fieldAccessor = typeof props.field.value === 'string' ? props.field.value : '';
    const isMultipleChoiceSelect =
      MULTIPLE_CHOICE_OPERATORS.includes(props.operator) ||
      ARRAY_CAST_ACCESSOR_PATTERN.test(fieldAccessor);

    let pickedOptions: OptionValue[] = [];
    if (Array.isArray(props.value)) {
      pickedOptions = props.value;
    } else if (props.value && typeof props.value !== 'string') {
      pickedOptions = [props.value];
    }
    pickedOptions = pickedOptions.filter((option) => toOptionKey(option) !== '');

    // Picked values that a server search has pushed out of the options stay listed, so they can be unpicked.
    const listOptions: BehaviorConfigOption[] = [
      ...(options ?? []),
      ...pickedOptions.filter(
        (picked) => !options?.some((option) => toOptionKey(option) === toOptionKey(picked)),
      ),
    ];
    const menuOptions: SelectMenuOption[] = listOptions.map((option) => ({
      value: toOptionKey(option),
      title: option.label,
      keywords: option.id === undefined ? undefined : [String(option.id)],
    }));
    const pickedKeys = pickedOptions.map(toOptionKey);
    const findOption = (key: string) =>
      listOptions.find((option) => toOptionKey(option) === key);

    // add key prop that changes when operator changes to force component re-render
    const componentKey = `select-menu-${props.operator}-${props.field.value}`;

    // several picks read as one line in the field, as they do in the menu, not "2 selected"
    const pickedSummary =
      isMultipleChoiceSelect && pickedOptions.length
        ? pickedOptions.map((option) => option.label).join(', ')
        : undefined;

    return (
      <FieldsContainer fillCell={isCondition}>
        {actionPrefix}
        <SelectMenu
          key={componentKey}
          label={props.field.label ?? 'Value'}
          variant="field"
          size="small"
          options={menuOptions}
          placeholder={isCondition ? 'Value' : undefined}
          triggerLabel={pickedSummary}
          tooltip={pickedSummary}
          multiple={isMultipleChoiceSelect}
          value={isMultipleChoiceSelect ? undefined : (pickedKeys[0] ?? null)}
          values={isMultipleChoiceSelect ? pickedKeys : undefined}
          searchValue={onInputChange ? inputValue : undefined}
          onSearchChange={
            onInputChange
              ? (query) => {
                  setInputValue(query);
                  onInputChange?.(query);
                }
              : undefined
          }
          loading={isLoadingOptions}
          disabled={props.disabled}
          sx={isCondition ? { width: '100%', minWidth: 0 } : { minWidth: '8rem' }}
          onChange={(key) => {
            const picked = findOption(key);
            if (picked) {
              props.handleOnChange(picked);
            }
          }}
          onValuesChange={(keys) =>
            props.handleOnChange(keys.map(findOption).filter(isNonNullish))
          }
        />
      </FieldsContainer>
    );
  }

  if (props.field.uiType === FormControlType.Date) {
    return (
      <FieldsContainer fillCell={isCondition}>
        <FormControl size="small" fullWidth={isCondition}>
          <RulesDatePicker
            onChange={({ isRollingDate, value }) => {
              if (isRollingDate) {
                props.handleOnChange(value);
                return;
              }

              props.handleOnChange((value as Dayjs).toISOString());
            }}
            value={props.value as string}
            timezone={timezone}
          />
        </FormControl>
      </FieldsContainer>
    );
  }

  if (props.field.uiType === FormControlType.DateTime) {
    return (
      <FieldsContainer>
        {props.field.controlType === RuleControlType.ActionControl ? 'to ' : null}
        <FormControl size="small">
          <RulesDateTimePicker
            value={props.value as string}
            timezone={timezone}
            onChange={(isoValue) => props.handleOnChange(isoValue)}
          />
        </FormControl>
      </FieldsContainer>
    );
  }

  if (props.field.uiType === FormControlType.Textarea) {
    return (
      <FieldsContainer fillCell={isCondition}>
        {actionPrefix}
        <ConditionTextField
          className={props.className}
          title={props.title}
          multiline
          minRows={3}
          maxRows={8}
          value={toTextValue(props.value)}
          disabled={props.disabled}
          onChange={props.handleOnChange}
          sx={textFieldSx}
        />
      </FieldsContainer>
    );
  }

  return <MaterialValueEditor {...props} extraProps={{ sx }} />;
};

function getFieldStyles(props: { field: SelectorValue; operator: DataConditionOperator }): {
  sx: Record<string, unknown>;
} {
  if (
    [DataConditionOperator.Between, DataConditionOperator.NotBetween].includes(
      props.operator,
    ) &&
    props.field.uiType === FormControlType.Number
  ) {
    return { sx: { width: '5rem' } };
  }

  return { sx: { minWidth: { xs: 0, sm: '12rem' } } };
}

/** The rules builder's value editor: a Custom schedule row edits its windows in place. */
export const ConditionValueEditor = (props: ComponentProps<typeof CustomValueEditor>) => {
  if (props.field?.conditionType === ConditionType.CustomSchedule) {
    return (
      <ScheduleConditionEditor
        className={props.className}
        windows={parseScheduleWindows(props.value)}
        onChange={props.handleOnChange}
        markInvalid
      />
    );
  }

  return <CustomValueEditor {...props} />;
};
